import { Dispatch, SetStateAction, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../ui/form';
import { IoEyeOutline, IoEyeOffOutline } from 'react-icons/io5';
import { Input } from '../ui/input';
import StyledButton from '../StyledHtmlElements/Button';
import { useToast } from '@/hooks/use-toast';
import { resetPassword, confirmResetPassword, confirmSignIn } from '@aws-amplify/auth';
import { validatePassword } from './helpers';

type Props = {
  setForgotPasswortEmail: Dispatch<SetStateAction<string>>;
  setIsForcePwChange: Dispatch<SetStateAction<boolean>>;
  forgotPasswortEmail: string;
  isForcePwChange: boolean;
};

const formSchema = z
  .object({
    code: z.string().length(6, { message: 'Der Code muss 6 Zeichen lang sein.' }).or(z.literal('')),
    password: z
      .string()
      .min(8, { message: 'Das Passwort muss mindestens 8 Zeichen lang sein.' })
      .max(50, { message: 'Das Passwort darf maximal 50 Zeichen lang sein.' })
      .refine(validatePassword, {
        message:
          'Das Passwort muss mindestens 1 Sonderzeichen, 1 Zahl, 1 Großbuchstaben und 1 Kleinbuchstaben enthalten.',
      }),
    confirmPassword: z.string(),
  })
  .superRefine((data, ctx) => {
    if (data.password !== data.confirmPassword) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Passwörter stimmen nicht überein.',
        path: ['confirmPassword'], // Optional: Gibt an, welches Feld betroffen ist
      });
    }
  });

const ConfirmResettForm = ({
  setForgotPasswortEmail,
  forgotPasswortEmail,
  isForcePwChange,
  setIsForcePwChange,
}: Props) => {
  const [showPasswort, setShowPasswort] = useState(false);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      code: '',
      password: '',
      confirmPassword: '',
    },
  });

  const backToLogin = () => {
    setForgotPasswortEmail('');
    setIsForcePwChange(false);
  };

  const resendCode = async () => {
    try {
      await resetPassword({ username: forgotPasswortEmail });
      toast({
        description: 'Bestätigungscode erfolgreich erneut gesendet.',
      });
    } catch (error) {
      let message = 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.';
      console.error('Error resending confirmation code:', error);

      if (error && typeof error === 'object' && 'message' in error) {
        message = error.message as string;
      }

      toast({
        variant: 'destructive',
        title: 'Fehler beim Bestätigungscode erneut senden',
        description: message,
      });
    }
  };

  async function setNewPasswort(values: z.infer<typeof formSchema>) {
    const request = isForcePwChange
      ? confirmSignIn({
          challengeResponse: values.password.trim(),
        })
      : confirmResetPassword({
          username: forgotPasswortEmail,
          newPassword: values.password.trim(),
          confirmationCode: values.code.trim(),
        });

    setLoading(true);

    try {
      await request;
      toast({
        description: 'Passwort erfolgreich zurückgesetzt.',
      });

      backToLogin();
    } catch (error) {
      let message = 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.';
      console.error('Error confirming reset password:', error);
      if (error && typeof error === 'object' && 'message' in error) {
        message = error.message as string;
      }
      toast({
        variant: 'destructive',
        title: 'Fehler beim Bestätigen des neuen Passworts',
        description: message,
      });
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <h1 className=" font-heading text-center tracking-widest text-xl">Neues Passwort erstellen</h1>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(setNewPasswort)} className="space-y-8">
          {!isForcePwChange && (
            <FormField
              control={form.control}
              name="code"
              render={({ field }) => (
                <FormItem>
                  <FormLabel htmlFor="code">Code</FormLabel>
                  <FormControl>
                    <Input id="code" placeholder="Gib den Code ein den Sie per Email erhalten haben." {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel htmlFor="set-account-password">Passwort</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Input
                      id="set-account-password"
                      placeholder="Neues Passwort"
                      type={showPasswort ? 'text' : 'password'}
                      {...field}
                    />

                    {showPasswort ? (
                      <IoEyeOutline
                        onClick={() => setShowPasswort(false)}
                        className="absolute right-3 top-[50%] -translate-y-1/2 opacity-75 "
                        size={20}
                      />
                    ) : (
                      <IoEyeOffOutline
                        onClick={() => setShowPasswort(true)}
                        className="absolute right-3 top-[50%] -translate-y-1/2 opacity-75 "
                        size={20}
                      />
                    )}
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="confirmPassword"
            render={({ field }) => (
              <FormItem>
                <FormLabel htmlFor="confirm-password">Passwort bestätigen</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Input
                      id="confirm-password"
                      placeholder="Passwort bestätigen"
                      type={showPasswort ? 'text' : 'password'}
                      {...field}
                    />

                    {showPasswort ? (
                      <IoEyeOutline
                        onClick={() => setShowPasswort(false)}
                        className="absolute right-3 top-[50%] -translate-y-1/2 opacity-75 "
                        size={20}
                      />
                    ) : (
                      <IoEyeOffOutline
                        onClick={() => setShowPasswort(true)}
                        className="absolute right-3 top-[50%] -translate-y-1/2 opacity-75 "
                        size={20}
                      />
                    )}
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="flex flex-col gap-4 pt-4">
            <StyledButton type="submit" className="w-full" loading={loading}>
              {isForcePwChange ? 'Passwort ändern' : 'Passwort zurücksetzen'}
            </StyledButton>

            {!isForcePwChange && (
              <StyledButton type="button" className="w-full" onClick={resendCode}>
                Code erneut senden
              </StyledButton>
            )}

            <StyledButton type="button" variant="link" className="w-full" onClick={backToLogin}>
              Zurück zum Login
            </StyledButton>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default ConfirmResettForm;
